import { useState, useEffect } from 'react';
import { getData } from './getData';
import { sendRideId } from './completeRide';
import { sendData } from '../formSubmission';
import DynamicPrice from './DynamicPrice';

export default function BookingTable() {
    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState({
        isOpen: false,
        bookingData: null,
        rowIndex: null,
        bookingId: null
    });

    const [searchPhone, setSearchPhone] = useState('');

    // --- Add Booking Modal State ---
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addBookingData, setAddBookingData] = useState({
        fromLocation: '',
        toLocation: '',
        date: '',
        time: '',
        passengers: 1,
        name: '',
        phone: '',
        email: '',
        message: 'Manual Booking'
    });

    // Helper function to convert time (seconds or string HH:MM:SS) to HH:MM format
    function formatTime(timeInput) {
        if (timeInput === null || timeInput === undefined) return "N/A";

        // Handle string format "HH:MM:SS" or "HH:MM"
        if (typeof timeInput === 'string' && timeInput.includes(':')) {
            return timeInput.substring(0, 5); // Just return "HH:MM"
        }

        // Handle numeric format (seconds since midnight)
        if (typeof timeInput === 'number' && !isNaN(timeInput)) {
            const secs = Math.floor(Math.max(0, timeInput));
            const hours = Math.floor(secs / 3600);
            const minutes = Math.floor((secs % 3600) / 60);
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        return String(timeInput);
    }


    const fetchBookings = async () => {
        setIsLoading(true);
        try {
            const data = await getData();
            console.log("Raw data fetched:", data);

            let normalized = [];
            if (data && Array.isArray(data)) {
                normalized = data.map(b => {
                    if (Array.isArray(b)) {
                        // Handle Tuple/Array format from backend
                        return {
                            id: b[0],
                            fromLocation: b[1],
                            toLocation: b[2],
                            date: b[3],
                            time: b[4],
                            returnDate: b[5],
                            passengers: b[6],
                            user: b[7],
                            phone: b[8],
                            email: b[9],
                            message: b[10],
                            createdAt: b[11]
                        };
                    } else if (b && typeof b === 'object') {
                        // Handle Object/Dictionary format
                        return {
                            id: b.id,
                            fromLocation: b.from_loc || b.fromLocation,
                            toLocation: b.to_loc || b.toLocation,
                            date: b.ride_date || b.date,
                            time: b.ride_time || b.time,
                            returnDate: b.return_date || b.returnDate,
                            passengers: b.passengers,
                            user: b.user || b.name,
                            phone: b.phone,
                            email: b.email,
                            message: b.message,
                            createdAt: b.created_at || b.createdAt
                        };
                    }
                    return null;
                }).filter(b => b !== null);

                // Sort normalized data
                normalized.sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    if (dateA < dateB) return -1;
                    if (dateA > dateB) return 1;

                    const getSeconds = (t) => {
                        if (typeof t === 'number') return t;
                        if (typeof t === 'string' && t.includes(':')) {
                            const [h, m, s] = t.split(':').map(Number);
                            return (h * 3600) + (m * 60) + (s || 0);
                        }
                        return 0;
                    };
                    return getSeconds(a.time) - getSeconds(b.time);
                });
            }

            console.log("Normalized data:", normalized);
            setBookings(normalized);

            if (normalized.length === 0) {
                setError("No booking data available. Enable the notification and we will let you know");
            } else {
                setError(null);
            }
        } catch (err) {
            console.error("Failed to fetch bookings:", err);
            setError("Failed to load data. Please check the network connection.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const handleCompleteBooking = (rowIndex, bookingId) => {
        // Find the booking in the sorted list by ID if indices shifted, 
        // but rowIndex passed from map should be correct for the current render.
        // Safer to find by ID if we were doing complex updates, but simple filter is fine.
        const booking = bookings[rowIndex];
        setModal({
            isOpen: true,
            bookingData: booking,
            rowIndex: rowIndex,
            bookingId: bookingId
        });
    };

    const confirmComplete = async () => {
        const { rowIndex, bookingId } = modal;

        try {
            const success = await sendRideId(bookingId);
            if (success) {
                // Remove the completed booking from the local state
                setBookings(prevBookings => prevBookings.filter(b => b[0] !== bookingId));
                setModal({ isOpen: false, bookingData: null, rowIndex: null, bookingId: null });
            } else {
                alert(`Operation failed for booking ${bookingId}. The server did not confirm completion. REFRESH THE TABLE`);
            }
        } catch (error) {
            alert(`Failed to complete booking ${bookingId}. Error: ${error.message || 'Unknown error'}`);
        }
    };

    const closeModal = () => {
        setModal({ isOpen: false, bookingData: null, rowIndex: null, bookingId: null });
    };

    // --- Manual Booking Handlers ---
    const handleAddBookingChange = (e) => {
        const { name, value } = e.target;
        setAddBookingData(prev => ({ ...prev, [name]: value }));
    };

    const submitManualBooking = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const { fromLocation, toLocation, date, time, passengers, name, phone, email, message } = addBookingData;

        // Basic validation
        if (!fromLocation || !toLocation || !date || !time || !name || !phone) {
            alert("Please fill in all required fields.");
            setIsSubmitting(false);
            return;
        }

        try {
            // sendData signature: from, to, car, distance, price, duration, date, time, message, user, phone, return_date, passengers
            // We'll use defaults for car, distance, price, duration as they are not calculated here manually
            const result = await sendData(
                fromLocation,
                toLocation,
                "standard", // Default car
                0, // 0 distance
                [0, 0], // 0 price
                "0 mins", // 0 duration
                date,
                time,
                message,
                name,
                phone,
                email, // Added email
                null, // no return date support in simple manual form for now
                passengers
            );

            if (result.status === "success") {
                // Refresh list
                await fetchBookings();
                setIsAddModalOpen(false);
                setAddBookingData({
                    fromLocation: '',
                    toLocation: '',
                    date: '',
                    time: '',
                    passengers: 1,
                    name: '',
                    phone: '',
                    email: '',
                    message: 'Manual Booking'
                });
            } else {
                alert(`Error: ${result.message}`);
            }
        } catch (error) {
            console.error("Manual booking error:", error);
            alert("Failed to create booking.");
        } finally {
            setIsSubmitting(false);
        }
    };


    // Filter bookings based on search phone
    const filteredBookings = bookings.filter(booking => {
        if (!searchPhone) return true;
        const phone = booking.phone;
        return phone && phone.toString().includes(searchPhone);
    });

    if (isLoading && !bookings.length) return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
            <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
                <p className="text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Loading bookings...</p>
            </div>
        </div>
    );
    if (error && (!bookings || !bookings.length)) return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-4">
                <div className="text-purple-600 text-5xl mb-4 text-center">📭</div>
                <p className="text-xl font-bold text-gray-800 text-center mb-2">Status Update</p>
                <p className="text-gray-600 text-center mb-6">{error}</p>
                <div className="flex gap-3">
                    <button
                        onClick={fetchBookings}
                        className="flex-1 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all shadow-lg font-semibold"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex-1 flex items-center justify-center px-6 py-3 bg-white text-purple-600 border-2 border-purple-100 rounded-xl hover:bg-purple-50 transition-all font-semibold"
                    >
                        + Add
                    </button>
                </div>
            </div>
            {/* Render Add Modal even in error state to allow recovery/manual entry */}
            {isAddModalOpen && renderAddBookingModal()}
        </div>
    );

    function renderAddBookingModal() {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all animate-fade-in-up flex flex-col max-h-[90vh]">
                    <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 px-6 py-4 flex justify-between items-center shrink-0">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <span>📝</span> Add Manual Booking
                        </h3>
                        <button onClick={() => setIsAddModalOpen(false)} className="text-white/80 hover:text-white">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto">
                        <form onSubmit={submitManualBooking} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                                    <input type="date" name="date" required value={addBookingData.date} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Time</label>
                                    <input type="time" name="time" required value={addBookingData.time} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pickup (From)</label>
                                <input type="text" name="fromLocation" required value={addBookingData.fromLocation} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" placeholder="Airport, Hotel..." />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dropoff (To)</label>
                                <input type="text" name="toLocation" required value={addBookingData.toLocation} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" placeholder="Address..." />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Client Name</label>
                                    <input type="text" name="name" required value={addBookingData.name} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                                    <input type="email" name="email" required value={addBookingData.email} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Phone</label>
                                    <input type="tel" name="phone" required value={addBookingData.phone} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Passengers</label>
                                    <select name="passengers" value={addBookingData.passengers} onChange={handleAddBookingChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none">
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                            </div>

                            <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 mt-4">
                                {isSubmitting ? 'Saving...' : 'Save Booking'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-4 md:py-8">
            <div className="w-full max-w-7xl mx-auto px-4">
                {/* Header Section */}
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-6 md:p-8 mb-6 md:mb-8 relative overflow-hidden animate-fade-in-up">
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                    <div className="relative">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="text-center md:text-left">
                                <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                                    <span className="text-3xl md:text-4xl">🚕</span>
                                    <h1 className="text-2xl md:text-4xl font-bold text-white">Booking Dashboard</h1>
                                </div>
                                <div className="mt-2 flex items-center justify-center md:justify-start gap-3 flex-wrap">
                                    <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                                        <span className="text-white text-sm font-semibold">📊 Total: {bookings.length}</span>
                                    </div>
                                    <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                                        <span className="text-white text-sm font-semibold">✨ Active</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-2 w-full md:w-auto">
                                <button
                                    onClick={() => setIsAddModalOpen(true)}
                                    className="flex-1 md:flex-none flex items-center justify-center px-4 py-2 bg-white/20 backdrop-blur-md text-white border border-white/30 rounded-xl hover:bg-white/30 transition-all shadow-lg font-semibold"
                                >
                                    <span className="mr-2 text-lg">+</span> Add Booking
                                </button>
                                <button
                                    onClick={fetchBookings}
                                    className="flex-1 md:flex-none flex items-center justify-center px-4 py-2 bg-white text-purple-600 rounded-xl hover:bg-gray-100 transition-all shadow-lg font-semibold"
                                    disabled={isLoading}
                                >
                                    <svg className={`w-5 h-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Bar Section */}
                <div className="mb-6 animate-fade-in-up stagger-1">
                    <div className="relative max-w-md mx-auto md:mx-0">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search by phone number..."
                            className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-md transition-all duration-200"
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(e.target.value)}
                        />
                    </div>
                </div>

                {/* Desktop View: Table */}
                <div className="hidden md:block bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border-2 border-white animate-fade-in-up stagger-2">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gradient-to-r from-purple-600 to-pink-600">
                                <tr>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Date/Time</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">From</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">To</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Pax</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Client / Email</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Phone</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Price</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Notes</th>
                                    <th className="px-6 py-4 text-sm font-bold text-white uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.map((b, i) => (
                                    <tr key={i} className="even:bg-gradient-to-r even:from-purple-50/50 even:to-pink-50/50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200 border-b border-gray-100">
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            <div className="font-bold text-purple-700">{b.date}</div>
                                            <div className="text-gray-500 text-xs">{formatTime(b.time)}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={b.fromLocation}>{b.fromLocation}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={b.toLocation}>{b.toLocation}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 text-center font-semibold">{b.passengers}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-800">
                                            <div className="font-bold">{b.user}</div>
                                            <div className="text-xs text-gray-500 lowercase">{b.email}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700">{b.phone}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 font-bold"><DynamicPrice fromLocation={b.fromLocation} toLocation={b.toLocation} passengers={b.passengers} /></td>
                                        <td className="px-6 py-4 text-sm text-gray-700 max-w-[150px] truncate" title={b.message}>{b.message}</td>
                                        <td className="px-6 py-4 text-sm">
                                            <button
                                                onClick={() => handleCompleteBooking(i, b.id)}
                                                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-semibold text-sm flex items-center gap-2"
                                            >
                                                ✅ Complete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile View: Cards */}
                <div className="md:hidden space-y-4 animate-fade-in-up stagger-2">
                    {filteredBookings.map((b, i) => (
                        <div key={i} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden relative">
                            {/* Color Bar / Status indicator */}
                            <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-purple-500 to-pink-500"></div>

                            <div className="p-4 pl-5">
                                {/* Top Row: Date/Time & Status/Action */}
                                <div className="flex justify-between items-start mb-3 border-b border-gray-100 pb-2">
                                    <div>
                                        <div className="text-lg font-bold text-gray-800">{b.date}</div>
                                        <div className="text-sm text-purple-600 font-semibold">{formatTime(b.time)}</div>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <DynamicPrice fromLocation={b.fromLocation} toLocation={b.toLocation} passengers={b.passengers} />
                                        <div className="text-xs font-bold px-2 py-1 bg-gray-100 text-gray-500 rounded-lg">
                                            ID: {b.id}
                                        </div>
                                    </div>
                                </div>

                                {/* Main Info: From -> To */}
                                <div className="space-y-3 mb-4">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 min-w-[16px]">🟢</div>
                                        <div>
                                            <div className="text-xs text-gray-400 font-bold uppercase">From</div>
                                            <div className="text-sm font-medium text-gray-800 leading-snug">{b.fromLocation}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 min-w-[16px]">🔴</div>
                                        <div>
                                            <div className="text-xs text-gray-400 font-bold uppercase">To</div>
                                            <div className="text-sm font-medium text-gray-800 leading-snug">{b.toLocation}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer: Client details & Action */}
                                <div className="bg-gray-50 -mx-4 -mb-4 p-4 flex flex-col gap-3">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">👤</div>
                                            <div>
                                                <div className="text-sm font-bold text-gray-800">{b.user}</div>
                                                <div className="text-xs text-blue-600 font-medium">{b.email}</div>
                                                <div className="text-xs text-gray-500">{b.phone}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200">
                                            <span>👥</span>
                                            <span className="font-bold text-gray-700">{b.passengers}</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleCompleteBooking(i, b.id)}
                                        className="w-full py-2.5 bg-green-500 text-white font-bold rounded-xl shadow-md active:scale-95 transition-transform"
                                    >
                                        ✅ Mark Complete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>


                {/* Empty State */}
                {bookings.length === 0 && !isLoading && !error && (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-4">📭</div>
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">No Bookings Yet</h3>
                        <p className="text-gray-500 mb-6">New bookings will appear here.</p>
                        <button onClick={() => setIsAddModalOpen(true)} className="px-6 py-2 bg-purple-600 text-white rounded-lg font-semibold">
                            Add First Booking
                        </button>
                    </div>
                )}

                {/* Confirmation Modal (shared) */}
                {modal.isOpen && modal.bookingData && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all animate-fade-in-up">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 px-8 py-6 relative overflow-hidden">
                                <div className="absolute inset-0 bg-black opacity-10"></div>
                                <div className="relative flex items-center justify-center gap-3">
                                    <span className="text-4xl">🚕</span>
                                    <h3 className="text-2xl font-bold text-white">Complete Ride</h3>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-8">
                                <p className="text-gray-600 text-center mb-6">
                                    Are you sure you want to mark this ride as completed?
                                </p>

                                {/* Client Information Cards */}
                                <div className="space-y-4 mb-6">
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl">
                                                👤
                                            </div>
                                            <div>
                                                <p className="text-xs text-blue-600 font-semibold uppercase">Client Name</p>
                                                <p className="text-lg font-bold text-gray-800">
                                                    {modal.bookingData.user || "N/A"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={closeModal}
                                        className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmComplete}
                                        className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                    >
                                        ✅ Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add Booking Modal */}
                {isAddModalOpen && renderAddBookingModal()}
            </div>
        </div>
    );
}